import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { classNames } from '../../framework/utils'
import { hasExposedNamespace } from '../../framework/microApps'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import AppLayout from './AppLayout'
import { HubHiddenNav } from './BottomNav'
import { useInactivityTimeout, useCurrentTsgConfig } from './InactivityTimeout'
import { getDefaultRoute } from './defaultRedirect'
import LeftNav from './LeftNav'
import {
  debouncedCacheNavDisplay,
  ensureAbsoluteRoute,
  getOverriddenNav,
  loadingLeftNavItems,
  useActiveItems,
  useEnsureShellPathReload,
  useNavDisplayItems,
  usePendoIdentity,
  useReadyToRenderAndClearMask,
  useSaveLastVisitedUrl,
} from './navUtils'
import { LoginFailed } from '../common/FullscreenError'
import RouteViewer from './RouteViewer'
import { MemoComponentWrapper } from './RouteWrapper'
import { useLoggedInPrompt } from './LoggedInPromptModal'
import NavTopBar from './NavTopBar'
import RightSidebar from './RightSidebar'

import type { VFC, ReactElement } from 'react'
import type { MainState, StoreState, OidcState, StateRef } from '../../framework/stateTypes'
import type { NavItem } from '../../framework/microAppTypes'

type NavItemArrayWithExtra = NavItem[] & {
  logo?: string
  collapsedLogo?: string
  matchedPath?: string
}
type MainProps = {
  state: StoreState
  main: MainState | undefined
  oidc: OidcState | undefined
  stateRef: StateRef
}
const Main: VFC<MainProps> = ({ state, main, oidc, stateRef }): ReactElement => {
  const location = useLocation()
  const { fxVars, navRoutes, auth, navDisplayItemsRef, leftMainRoutes, leftMainNavigation, leftMainActions } = state
  const { loggedInUser = auth?.user } = main || {}

  const leftNavigation = useMemo(() => {
    if (!leftMainNavigation?.length) {
      return [] as NavItemArrayWithExtra
    }
    let overriddenNav = getOverriddenNav(leftMainNavigation, location?.pathname)
    if (!overriddenNav?.length) {
      overriddenNav = leftMainNavigation
    }
    const leftNav = ensureAbsoluteRoute(overriddenNav) as NavItemArrayWithExtra
    const { logo, collapsedLogo, matchedPath } = overriddenNav as NavItemArrayWithExtra
    const logoUrlRegex = /^(?:https?:|data:|\/)/
    if (logo && logoUrlRegex.test(logo)) {
      leftNav.logo = logo
    }
    if (collapsedLogo && logoUrlRegex.test(collapsedLogo)) {
      leftNav.collapsedLogo = collapsedLogo
    }
    if (matchedPath) {
      leftNav.matchedPath = matchedPath
    }
    return leftNav
  }, [location?.pathname, leftMainNavigation])

  const [logo, collapsedLogo] = useMemo(() => {
    const { logo = fxVars.logo, collapsedLogo = fxVars.collapsed_logo } = leftNavigation
    return [logo as string | undefined, collapsedLogo as string | undefined]
  }, [leftNavigation, fxVars])

  const leftNavDisplayItems = useNavDisplayItems(leftNavigation, leftMainActions, state)
  const [activeNavItem, activeSubNavItem] = useActiveItems(leftNavDisplayItems)
  navDisplayItemsRef.current = leftNavDisplayItems

  usePendoIdentity(loggedInUser, stateRef, fxVars)

  const readyState = useReadyToRenderAndClearMask(state, fxVars)
  const readyToRenderRoutes = readyState === 'render'

  useEnsureShellPathReload(readyToRenderRoutes ? fxVars.shell_path_overrides : undefined)
  useSaveLastVisitedUrl(readyToRenderRoutes && !fxVars.hub)
  useInactivityTimeout(readyToRenderRoutes && !state.loggingOut && fxVars.inactivity_timeout, useCurrentTsgConfig(state.auth))
  useLoggedInPrompt(state.loggedInPrompt)
  useEffect(() => {
    if (!readyToRenderRoutes || !auth?.tsg_id || !leftMainNavigation ||
      !fxVars.use_left_nav_cache) {
      return
    }
    debouncedCacheNavDisplay(auth.tsg_id, leftMainNavigation)
  }, [readyToRenderRoutes, auth.tsg_id, leftMainNavigation, leftNavigation.matchedPath, fxVars.use_left_nav_cache])

  const topItems = useMemo(() => {
    if (readyState === 'render') {
      return leftNavDisplayItems.topItems
    }
    if (readyState === 'loading') {
      return [] as NavItem[] // no left nav yet
    }
    return ((leftMainActions as any)._nav_early_init || loadingLeftNavItems) as NavItem[]
  }, [readyState, leftMainActions, leftNavDisplayItems.topItems])

  if (state.loginFailed?.message) {
    return <LoginFailed message={state.loginFailed.message} />
  }
  if (readyState === 'loading' && (auth.initializing || !(fxVars.hub || fxVars.msp))) {
    return <LoadingPlaceholder />
  }

  const leftNav = fxVars.hub || fxVars.nav_content_only ? (
    <HubHiddenNav fxVars={fxVars} state={state} />
  ) : <LeftNav
    leftNavDisplayItems={topItems}
    state={state}
    fxVars={fxVars}
    oidc={oidc}
    logo={logo}
    collapsedLogo={collapsedLogo}
    activeNavItem={activeNavItem}
    activeSubNavItem={activeSubNavItem}
  />

  const routes = readyToRenderRoutes ? (
    <RouteViewer
      navigation={navRoutes}
      leftMainRoutes={leftMainRoutes}
      state={state}
      stateRef={stateRef}
      defaultRoute={getDefaultRoute(auth)}
      RouteWrapper={MemoComponentWrapper}
      fxVars={fxVars}
    />
  ) : <div className='twsf-w-full'><LoadingPlaceholder /></div>

  return (
    <AppLayout
      leftNav={leftNav}
      topbar={auth && hasExposedNamespace('scm_main_layout.content_top') ? <NavTopBar stateRef={stateRef} location={location} auth={auth} fxVars={fxVars} /> : null}
      rightSidebar={hasExposedNamespace('scm_main_layout.content_right') && <RightSidebar stateRef={stateRef} state={state} />}
      addBodyClassName={classNames(
        'twsf-bg-gray-100 dark:twsf-bg-gray-950',
        !fxVars.nav_content_only && 'twsf-overflow-auto',
      )}
    >
      {routes}
    </AppLayout>
  )
}

export default Main
